<script setup lang="ts">
import { rpcHttpKey } from "@uicomps/common/helpers";
import { computed, ref } from "vue";
import { carouselAddresses, signupData, isCharacterAvailable } from "../data";
import useStepEmits from "./useStepEmits";
import InfoTip from "./InfoTip.vue";
import { useI18n } from "vue-i18n";
defineEmits(["stepComplete", "stepLoading"]);

const { emitStepCompleted, emitStepLoading } = useStepEmits();
// const isUsernameAvailable = ref(false);
const hasError = ref(false);
const errorText = ref("");
const { t } = useI18n();

const selectedCarouselIndex = ref(0);
const safeNextCarouselIndex = computed(() => {
  if (selectedCarouselIndex.value + 1 === carouselAddresses.length) {
    return 0;
  }
  return selectedCarouselIndex.value + 1;
});
const safePreviousCarouselIndex = computed(() => {
  if (selectedCarouselIndex.value === 0) {
    return carouselAddresses.length - 1;
  }
  return selectedCarouselIndex.value - 1;
});

function setSelectedIndex(index: number) {
  selectedCarouselIndex.value = index;
  signupData.selectedCharacter = carouselAddresses[index];
}

let checkedUsernames = {};
let checkUsernameAvailabilityTimeout;
function handleInput() {
  hasError.value = false;
  emitStepCompleted(false);
  clearTimeout(checkUsernameAvailabilityTimeout);

  let isUsernameAvailable = checkedUsernames[signupData.username];
  if (isUsernameAvailable !== undefined) {
    handleIsUsernameAvailable(isUsernameAvailable);
    return;
  }

  checkUsernameAvailabilityTimeout = setTimeout(async () => {
    if (signupData.username.length > 3) {
      checkIsUsernameAvailable();
    } else {
      hasError.value = true;
      errorText.value = t("pages.signup.character.error.username.too-short");
    }
  }, 300);
}
function handleIsUsernameAvailable(isUsernameAvailable: boolean) {
  if (!isUsernameAvailable) {
    errorText.value = t("pages.signup.character.error.username.already-taken");
  }
  hasError.value = !isUsernameAvailable;
  emitStepCompleted(isUsernameAvailable);
}
async function checkIsUsernameAvailable() {
  emitStepLoading(true);
  const isUsernameAvailable = await rpcHttpKey("UserIsUsernameAvailable", {
    username: signupData.username,
  });
  emitStepLoading(false);

  checkedUsernames[signupData.username] = isUsernameAvailable;
  handleIsUsernameAvailable(isUsernameAvailable);
}
</script>
<template>
  <div class="character-carousel flex center gap--s padding-t--sm pos--rel">
    <nft-card
      :address="carouselAddresses[safePreviousCarouselIndex]"
      class="pos--abs static"
      :class="
        isCharacterAvailable(carouselAddresses[safePreviousCarouselIndex])
          ? ''
          : ' unavailable'
      "
    />
    <nft-card
      :address="carouselAddresses[safeNextCarouselIndex]"
      class="pos--abs static"
      :class="
        isCharacterAvailable(carouselAddresses[safeNextCarouselIndex])
          ? ''
          : ' unavailable'
      "
    />
    <nft-card
      :address="carouselAddresses[selectedCarouselIndex]"
      class="active"
      :class="
        isCharacterAvailable(carouselAddresses[selectedCarouselIndex])
          ? ''
          : ' unavailable'
      "
    />
    <div class="flex gap--xxl pos--abs z-1">
      <button
        class="highlighted--l blue-300 padding-l--xsm padding-r--s flex center gap--s border-radius--"
        @click="setSelectedIndex(safePreviousCarouselIndex)"
      >
        <object data="/assets/overlay/icons/glyphs/chevron-pixel.svg">></object>
      </button>
      <button
        class="highlighted--l blue-300 padding-l--s padding-r--xsm flex center gap--s"
        @click="setSelectedIndex(safeNextCarouselIndex)"
      >
        <object data="/assets/overlay/icons/glyphs/chevron-pixel-right.svg">
          >
        </object>
      </button>
    </div>
  </div>
  <info-tip>{{ t("pages.signup.character.tip") }}</info-tip>
  <label v-depixel for="username">{{ t("global.username") }}</label>
  <text-input
    name="username"
    v-model="signupData.username"
    :errorText="hasError ? errorText : ''"
    @input="handleInput"
  />

  <span v-depixel class="warning color-text--orange-350">
    {{ t("pages.signup.character.warning") }}
  </span>
</template>
<style scoped lang="scss">
.character-carousel {
  overflow-x: hidden;
  .unavailable {
    filter: grayscale(100%);
  }
  &::before,
  &::after {
    position: absolute;
    display: block;
    content: "";
    width: 25%;
    height: 100%;
    top: 0;
    z-index: 1;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #f4f4f4 15%, rgba(0, 0, 0, 0));
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #f4f4f4 15%, rgba(0, 0, 0, 0));
  }
  .z-1 {
    z-index: 2;
    button {
      width: 48px;
    }
  }
  .basic-card:not(.active):deep() {
    .name {
      display: none;
    }
  }
  & > div:not(.active) {
    &:first-of-type {
      translate: calc(-100% - get-size(s));
      & + div {
        translate: calc(100% + get-size(s));
      }
    }
  }
  .gap--xxl {
    gap: 200px;
    @include respond(mobile) {
      z-index: 2;
      translate: 0 0 1px;
      button {
        height: 48px;
        width: 48px;
      }
    }
  }
}

.warning {
  @include respond(mobile-portrait) {
    display: none;
  }
}
</style>
